/**
 * Full-viewport gadget / Cap'n Web app viewer (Open). Hides the rail and chat.
 * Edit-beside-chat is only via "Edit" here or "Edit in a chat" on Extensions — not Open.
 */

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ForumIcon from '@mui/icons-material/Forum'
import ShareIcon from '@mui/icons-material/Share'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useRosShell } from '../RosShellContext'
import { MicroAppDock } from '../components/MicroAppDock'
import { ShareUsersDialog } from '../components/ShareUsersDialog'
import type { InviteType } from './extensionInvitesApi'

export interface GadgetViewerState {
  displayName?: string
  /** Cap'n Web Apps get Add Users; Gadgets do not. */
  kind?: 'gadget' | 'app'
  registryEntryId?: string
}

export function GadgetViewerPage() {
  const shell = useRosShell()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const routedState = (location.state || {}) as GadgetViewerState
  const displayName = routedState.displayName ?? id ?? ''
  const isApp = routedState.kind === 'app'
  const [invite, setInvite] = useState<{ type: InviteType } | null>(null)
  const [snackbar, setSnackbar] = useState<string | null>(null)

  if (!id) return null

  const editInChat = () => {
    shell.openMicroApp(id, displayName)
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, gap: 1 }}
      >
        <IconButton size="small" onClick={() => navigate('/extensions')} aria-label="Back to Extensions">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {displayName || id}
        </Typography>
        <Button
          size="small"
          startIcon={<ForumIcon />}
          onClick={editInChat}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          Edit
        </Button>
        <Button
          size="small"
          startIcon={<ShareIcon />}
          onClick={() => setInvite({ type: 'share_blueprint' })}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          Share
        </Button>
        {isApp && (
          <Button
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={() => setInvite({ type: 'add_member' })}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Add Users
          </Button>
        )}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <MicroAppDock
          definitionId={id}
          displayName={displayName || undefined}
          apiBaseUrl={shell.apiBaseUrl}
          token={shell.token}
        />
      </Box>

      {invite && (
        <ShareUsersDialog
          open
          onClose={() => setInvite(null)}
          apiBaseUrl={shell.apiBaseUrl}
          token={shell.token}
          selfEmail={shell.email}
          type={invite.type}
          definitionId={id}
          displayName={displayName || id}
          onSent={(count) => {
            setInvite(null)
            setSnackbar(
              invite.type === 'add_member'
                ? `Invited ${count} user${count === 1 ? '' : 's'} to view this app`
                : `Shared with ${count} user${count === 1 ? '' : 's'}`,
            )
          }}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2500}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />
    </Box>
  )
}
